import React, { useMemo } from 'react';
import { Sparkles } from 'lucide-react';

// Hand-picked quotes that sit well with reflective practice. Rotated
// deterministically by day-of-year so every visit on the same day shows the
// same one — no LLM call, no flicker, no surprise on refresh.
const QUOTES = [
  { text: 'We do not learn from experience. We learn from reflecting on experience.', who: 'John Dewey' },
  { text: 'The unexamined life is not worth living.', who: 'Socrates' },
  { text: 'Knowing yourself is the beginning of all wisdom.', who: 'Aristotle' },
  { text: 'Between stimulus and response there is a space. In that space is our power to choose.', who: 'Viktor Frankl' },
  { text: 'Your visions will become clear only when you can look into your own heart.', who: 'Carl Jung' },
  { text: "What you seek is seeking you.", who: 'Rumi' },
  { text: 'The journey of a thousand miles begins with one step.', who: 'Lao Tzu' },
  { text: 'Be kind, for everyone you meet is fighting a hard battle.', who: 'Plato' },
  { text: 'Small daily improvements over time lead to stunning results.', who: 'Robin Sharma' },
  { text: 'Awareness is the greatest agent for change.', who: 'Eckhart Tolle' },
  { text: 'No pressure, no diamonds.', who: 'Thomas Carlyle' },
  { text: 'You have power over your mind — not outside events. Realise this, and you will find strength.', who: 'Marcus Aurelius' },
  { text: 'Watch your thoughts, they become your words; watch your words, they become your actions.', who: 'Lao Tzu' },
  { text: "Don't judge each day by the harvest you reap but by the seeds that you plant.", who: 'Robert Louis Stevenson' },
  { text: 'What we plant in the soil of contemplation, we shall reap in the harvest of action.', who: 'Meister Eckhart' },
  { text: 'Almost everything will work again if you unplug it for a few minutes, including you.', who: 'Anne Lamott' },
  { text: 'The first step toward change is awareness. The second step is acceptance.', who: 'Nathaniel Branden' },
  { text: "You can't go back and change the beginning, but you can start where you are and change the ending.", who: 'C.S. Lewis' },
  { text: 'Be patient with yourself. Self-growth is tender; it’s holy ground.', who: 'Stephen Covey' },
  { text: 'The mind is everything. What you think you become.', who: 'Buddha' },
  { text: 'It is during our darkest moments that we must focus to see the light.', who: 'Aristotle' },
  { text: 'Tell me, what is it you plan to do with your one wild and precious life?', who: 'Mary Oliver' },
  { text: 'Knowing others is intelligence; knowing yourself is true wisdom.', who: 'Lao Tzu' },
  { text: 'When you change the way you look at things, the things you look at change.', who: 'Wayne Dyer' },
  { text: 'Out of clutter, find simplicity. From discord, find harmony.', who: 'Albert Einstein' },
  { text: "What lies behind us and what lies before us are tiny matters compared to what lies within us.", who: 'Ralph Waldo Emerson' },
  { text: "Don't ask what the world needs. Ask what makes you come alive, and go do it.", who: 'Howard Thurman' },
  { text: 'The privilege of a lifetime is to become who you truly are.', who: 'Carl Jung' },
  { text: 'Progress, not perfection.', who: 'Anonymous' },
  { text: 'Quiet the mind, and the soul will speak.', who: 'Ma Jaya Sati Bhagavati' },
  { text: 'A river cuts through rock, not because of its power, but because of its persistence.', who: 'James N. Watkins' },
];

const dayOfYear = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = (now - start) + ((start.getTimezoneOffset() - now.getTimezoneOffset()) * 60 * 1000);
  return Math.floor(diff / 86400000);
};

const DailyQuote = () => {
  const quote = useMemo(() => QUOTES[dayOfYear() % QUOTES.length], []);

  return (
    <div
      className="relative bg-gradient-to-r from-brand-accent/8 to-brand-accent/[0.03] border border-brand-border rounded-xl px-5 py-4 sm:px-6 sm:py-5 mb-4 sm:mb-6 overflow-hidden"
      data-testid="daily-quote"
    >
      <div className="absolute -top-3 -left-3 w-16 h-16 bg-brand-accent/10 rounded-full blur-2xl pointer-events-none" />
      <div className="flex items-start gap-3 relative">
        <div className="shrink-0 mt-0.5">
          <Sparkles className="w-4 h-4 text-brand-accent" />
        </div>
        <div className="flex-1">
          <p className="text-sm sm:text-base text-brand-ink italic leading-relaxed" data-testid="daily-quote-text">
            “{quote.text}”
          </p>
          <p className="mt-1.5 text-xs text-brand-ink-muted" data-testid="daily-quote-author">
            — {quote.who}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DailyQuote;
